import { RefObject, useEffect, useRef } from 'react'

export type UseClickOutsideOptions = {
  enabled?: boolean
  shouldIgnoreTarget?: (el: Element | null) => boolean
}

/**
 * Invokes `handler` on `mousedown` when the target is outside all given refs.
 */
function useClickOutside(
  refs: RefObject<HTMLElement | null> | Array<RefObject<HTMLElement | null>>,
  handler: (event: MouseEvent) => void,
  options?: UseClickOutsideOptions
): void {
  const { enabled = true, shouldIgnoreTarget } = options ?? {}

  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const ignoreRef = useRef(shouldIgnoreTarget)
  ignoreRef.current = shouldIgnoreTarget

  const refsRef = useRef(refs)
  refsRef.current = refs

  useEffect(() => {
    if (!enabled) {
      return
    }

    const onMouseDown = (event: MouseEvent) => {
      if (ignoreRef.current?.(event.target instanceof Element ? event.target : null)) return

      const target = event.target as Node | null
      const r = refsRef.current
      const refsArray = Array.isArray(r) ? r : [r]
      const isOutsideAll = refsArray.every(ref => ref.current == null || !ref.current.contains(target))

      if (isOutsideAll) {
        handlerRef.current(event)
      }
    }

    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [enabled])
}

export default useClickOutside
